import { cn } from '@repo/ui/lib/utils'
import { motion, useMotionValueEvent, useReducedMotion, useScroll } from 'motion/react'
import type React from 'react'
import { useState } from 'react'
import { Brand } from './brand'
import { MobileMenu, MobileToggle } from './mobile'
import type { NavigationContent } from './shared'
import { NavActionButton, NavLinksRenderer, useIsBelowBreakpoint } from './shared'

export const NavbarResizable: React.FC<{ content: NavigationContent; className?: string }> = ({
  content,
  className,
}) => {
  const { brand, links = [], actions = [], mobileBreakpoint = 768 } = content
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [active, setActive] = useState<string | null>(null)
  const { scrollY } = useScroll()
  const prefersReducedMotion = useReducedMotion()
  const isMobile = useIsBelowBreakpoint(mobileBreakpoint)

  useMotionValueEvent(scrollY, 'change', (latest) => {
    setScrolled(latest > 42)
  })

  return (
    <>
      <div className={cn('sticky top-4 z-50 w-full', className)}>
        <motion.nav
          className={cn(
            'mx-auto border border-border/55 bg-background/78 backdrop-blur-xl',
            'supports-[backdrop-filter]:bg-background/72',
            scrolled ? '[box-shadow:var(--web-shadow-lift)]' : '[box-shadow:var(--web-shadow-soft)]'
          )}
          animate={{
            width: !isMobile && scrolled ? 'min(100%, 80rem)' : '100%',
            y: scrolled ? 4 : 0,
            borderRadius: isMobile ? '14px' : scrolled ? '9999px' : '18px',
            rotateX: prefersReducedMotion ? 0 : scrolled ? 1.25 : 0,
            rotateY: prefersReducedMotion ? 0 : scrolled ? -0.45 : 0,
            transformPerspective: prefersReducedMotion ? 1000 : 1200,
          }}
          transition={{ duration: 0.42, ease: [0.25, 1, 0.5, 1] }}
          style={{
            minWidth: 'auto',
            transformStyle: 'preserve-3d',
          }}
        >
          <div className="relative z-10 mx-auto flex h-16 items-center justify-between px-4 sm:px-6">
            <Brand brand={brand} />

            <div className={cn('items-center', isMobile ? 'hidden' : 'flex')}>
              <NavLinksRenderer links={links} active={active} setActive={setActive} />
            </div>

            <div className={cn('items-center space-x-3', isMobile ? 'hidden' : 'flex')}>
              {actions.map((action, index) => (
                <NavActionButton key={index} action={action} />
              ))}
            </div>

            {isMobile && (
              <div className="flex items-center gap-2">
                <MobileToggle
                  isOpen={mobileOpen}
                  onToggle={() => setMobileOpen(!mobileOpen)}
                  mobileBreakpoint={mobileBreakpoint}
                />
              </div>
            )}
          </div>
        </motion.nav>
      </div>

      <MobileMenu isOpen={mobileOpen} onClose={() => setMobileOpen(false)} content={content} />
    </>
  )
}
